// frontend/src/common/ui/GridBasePayTable.jsx

import { Button } from "@common";
import { GRID_MAX_ROWS, validateBasePay, validateBasePayPct } from "@utils/practitionerFormUtils";

import { safeText } from "@utils/utils";

/**
 * Reusable Grid Base Pay table.
 *
 * Props:
 * - form: practitioner form object
 * - update: (key, value) => void
 * - submitting: boolean
 * - effectiveGridRows: number
 * - addGridRow: () => void
 * - gridIsValid: boolean (for showing global error)
 */
export default function GridBasePayTable({
  form,
  update,
  submitting,
  effectiveGridRows,
  addGridRow,
  gridIsValid,
}) {
  return (
    <div className="sm:col-span-2 mt-4">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-sm font-semibold text-palette-slate-primary">Grid Base Pay</div>
          <div className="text-xs text-palette-gray-secondary">
            Enter Base Pay and Percentage (0–100). You can add up to 4 rows.
          </div>
        </div>

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addGridRow}
          disabled={effectiveGridRows >= GRID_MAX_ROWS || submitting}
          className={
            effectiveGridRows >= GRID_MAX_ROWS || submitting
              ? "!bg-palette-gray-subtle !text-palette-gray-placeholder !border-palette-gray-divider cursor-not-allowed"
              : "!bg-white text-palette-indigo-heading !border-palette-indigo-border hover:!bg-palette-indigo-soft"
          }
          title="Add Base Pay row"
        >
          <span className="text-lg leading-none">+</span> Add row
        </Button>
      </div>

      <div className="overflow-auto rounded-xl border border-palette-gray-divider">
        <table className="min-w-full divide-y divide-palette-gray-divider">
          <thead className="bg-palette-gray-soft">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-palette-gray-heading uppercase tracking-wider">
                Base Pay
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-palette-gray-heading uppercase tracking-wider">
                Percentage (%)
              </th>
            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-palette-gray-divider">
            {Array.from({ length: effectiveGridRows }, (_, idx) => {
              const i = idx + 1;
              const payKey = `base_pay_${i}`;
              const pctKey = `base_pay_${i}_percentage`;

              const payRaw = safeText(form[payKey]).trim();
              const pctRaw = safeText(form[pctKey]).trim();
              const hasAny = payRaw !== "" || pctRaw !== "";

              // ✅ shared validation (no duplication)
              const payOk = validateBasePay(form[payKey], hasAny).ok;
              const pctOk = validateBasePayPct(form[pctKey], hasAny).ok;

              return (
                <tr key={i}>
                  <td className="px-4 py-3">
                    <input
                      type="number"
                      value={form[payKey]}
                      onChange={(e) => update(payKey, e.target.value)}
                      placeholder={`Base Pay ${i}`}
                      min="0"
                      step="0.01"
                      disabled={submitting}
                      className={[
                        "w-full h-10 rounded-lg border px-3 text-sm outline-none",
                        payOk
                          ? "border-palette-gray-border focus:ring-2 focus:ring-palette-indigo-hover"
                          : "border-palette-red-indicator focus:ring-2 focus:ring-palette-red-border",
                      ].join(" ")}
                    />
                  </td>

                  <td className="px-4 py-3">
                    <input
                      type="number"
                      value={form[pctKey]}
                      onChange={(e) => update(pctKey, e.target.value)}
                      placeholder={`Base Pay ${i} %`}
                      min="0"
                      max="100"
                      step="0.01"
                      disabled={submitting}
                      className={[
                        "w-full h-10 rounded-lg border px-3 text-sm outline-none",
                        pctOk
                          ? "border-palette-gray-border focus:ring-2 focus:ring-palette-indigo-hover"
                          : "border-palette-red-indicator focus:ring-2 focus:ring-palette-red-border",
                      ].join(" ")}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {!gridIsValid && (
        <div className="mt-2 text-xs text-palette-red-badge">
          Please enter valid Base Pay (≥ 0) and % (0–100).
        </div>
      )}
    </div>
  );
}
